import { EnvironmentProviders, inject, provideAppInitializer } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';

const RELOAD_MARKER = 'nle-cms.chunk-reload';

/**
 * A deploy replaces every hashed bundle at once and removes the previous ones. A tab that was
 * opened before the deploy still holds the old main bundle, so the dynamic import behind a lazy
 * route asks for a chunk filename that no longer exists on the server and the navigation fails.
 *
 * Reload once on that specific failure so the browser picks up the current index.html and bundles.
 * The marker keeps a genuinely broken chunk from turning into a reload loop: it survives the
 * reload and is only cleared once a navigation actually completes.
 */
export function provideChunkLoadRecovery(): EnvironmentProviders {
  return provideAppInitializer(() => {
    const router = inject(Router);

    router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        sessionStorage.removeItem(RELOAD_MARKER);
        return;
      }

      if (!(event instanceof NavigationError) || !isChunkLoadError(event.error)) {
        return;
      }

      // Already retried this URL after a reload, so the chunk is missing for another reason.
      if (sessionStorage.getItem(RELOAD_MARKER) === event.url) {
        return;
      }

      sessionStorage.setItem(RELOAD_MARKER, event.url);
      location.assign(event.url);
    });
  });
}

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name} ${error.message}` : String(error);

  return /ChunkLoadError|Loading chunk|error loading dynamically imported module|Importing a module script failed|Failed to fetch dynamically imported module/i.test(
    message,
  );
}
